// @ts-expect-error - SCSS imported for styling
import './copSiteDetails.scss'

import React, { useEffect, useState } from 'react'

import { ToastService } from '../../services/toast.service'
import { CopSiteDetailsSection } from './component/CopSiteDetailsSection'
import { ContactsConfig } from './component/EditContactsModal'
import {
    COP_SITE_DETAILS_ERROR,
    COP_SITE_DETAILS_EXPORT,
} from './constants/copSiteDetails.constants'
import {
    fetchSiteDetails,
    getSiteIdFromUrl,
    SiteDetailsResponse,
} from './services/copSiteDetails.service'

export interface DetailFieldConfig {
    displayName: string
    dataKey: string
    displayUrl: string
}

export interface ButtonConfig {
    buttonType: 'dropdown' | 'cta'
    buttonLabel: string
    buttonIcon: string
    buttonUrl: string
    buttonTooltip?: string // AEM: tooltip shown when this action is disabled
}

export type CopSiteDetailsProps = {
    exportLabel?: string
    enableTitle?: boolean
    enableMap?: boolean
    buttons?: string      // AEM: JSON string of ButtonConfig[]
    detailFields?: string // AEM: JSON string of DetailFieldConfig[]
    emailLabel?: string
    apiErrorMessage?: string
    enableContacts?: string  // AEM: 'true' | 'false'
    contactsConfig?: string  // AEM: JSON string of ContactsConfig
}


function parseJson<T>(raw?: string): T[] {
    if (!raw) return []
    try {
        const textarea = document.createElement('textarea')
        textarea.innerHTML = raw
        const decoded = textarea.value
        const parsed = JSON.parse(decoded)
        return Array.isArray(parsed) ? (parsed as T[]) : []
    } catch {
        return []
    }
}

function parseJsonObject<T>(raw?: string): T | null {
    if (!raw) return null
    try {
        const textarea = document.createElement('textarea')
        textarea.innerHTML = raw
        const decoded = textarea.value
        const parsed = JSON.parse(decoded) as unknown
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as T) : null
    } catch {
        return null
    }
}

const CopSiteDetails: React.FC<Readonly<CopSiteDetailsProps>> = ({
    exportLabel = COP_SITE_DETAILS_EXPORT.DEFAULT_LABEL,
    enableTitle = true,
    enableMap = true,
    buttons: buttonsRaw,
    detailFields: detailFieldsRaw,
    emailLabel,
    apiErrorMessage,
    enableContacts: enableContactsRaw,
    contactsConfig: contactsConfigRaw,
}) => {

    const [siteData, setSiteData] = useState<SiteDetailsResponse | null>(null)
    const [hasApiError, setHasApiError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const detailFields = parseJson<DetailFieldConfig>(detailFieldsRaw)
    const buttons = parseJson<ButtonConfig>(buttonsRaw)
    const enableContacts = enableContactsRaw === 'true'
    const contactsConfig = parseJsonObject<ContactsConfig>(contactsConfigRaw)

    useEffect(() => {
        const siteId = getSiteIdFromUrl()
        if (!siteId) {
            setHasApiError(true)
            ToastService.error(COP_SITE_DETAILS_ERROR.NO_SITE_ID)
            return
        }
        setIsLoading(true)
        fetchSiteDetails(siteId)
            .then(data => { if (data) setSiteData(data) })
            .catch((error: unknown) => {
                setHasApiError(true)
                const message = error instanceof Error ? error.message : ''
                ToastService.error(apiErrorMessage || message || COP_SITE_DETAILS_ERROR.FETCH_FAILED)
                window.console.error('CopSiteDetails: API error', error)
            })
            .finally(() => setIsLoading(false))
    }, [apiErrorMessage])

    return (
        <CopSiteDetailsSection
            exportLabel={exportLabel}
            enableTitle={enableTitle}
            enableMap={enableMap}
            detailFields={detailFields}
            buttons={buttons}
            emailLabel={emailLabel}
            siteData={siteData}
            hasApiError={hasApiError}
            isLoading={isLoading}
            enableContacts={enableContacts}
            contactsConfig={contactsConfig}
        />
    )
}

// Default export retained: registered as an AEM page/route component.
export default CopSiteDetails
